/**
 * Turnos Hooks - React Query hooks for turn management
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  crearTurno,
  getTurno,
  getPanelTurnos,
  llamarTurno,
  rellamarTurno,
  updateTurnoPrioridad,
  finalizarTurno,
  cancelarTurno,
  getEstadisticas,
  getTiempoEspera,
  getPuntosAtencion,
  getPuntosAtencionActivos,
  getPuntoAtencion,
  createPuntoAtencion,
  updatePuntoAtencion,
  deletePuntoAtencion,
  activatePuntoAtencion,
  deactivatePuntoAtencion,
  crearTurnoPublico,
  getPantallaPublica,
  type Turno,
  type TurnoListItem,
  type TurnoCreateData,
  type TurnoEstadisticas,
  type TiempoEspera,
  type PuntoAtencion,
  type PuntoAtencionCreateData,
  type PuntoAtencionUpdateData,
  type PantallaPublicaData,
} from "@/services/turnos";

// Re-export functions for useServerDataTable
export { getPuntosAtencionPaginated } from "@/services/turnos";

// Re-export types
export type {
  Turno,
  TurnoListItem,
  TurnoCreateData,
  TurnoEstadisticas,
  TiempoEspera,
  PuntoAtencion,
  PuntoAtencionCreateData,
  PuntoAtencionUpdateData,
  PantallaPublicaData,
};

// =============================================================================
// SHARED TYPES
// =============================================================================

/**
 * Opciones comunes para callbacks de mutaciones
 */
interface MutationCallbacks<TData = unknown> {
  /** Se ejecuta cuando la mutación es exitosa */
  onSuccess?: (data: TData) => void;
  /** Se ejecuta cuando hay un error */
  onError?: (error: Error) => void;
}

// =============================================================================
// PANTALLA PUBLICA HOOKS
// =============================================================================

/**
 * Hook to get public display data for a branch (no auth required)
 */
export function usePantallaPublica(
  sucursalCodigo: string,
  options?: { refetchInterval?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.turnos.pantallaPublica(sucursalCodigo),
    queryFn: () => getPantallaPublica(sucursalCodigo),
    enabled: (options?.enabled ?? true) && !!sucursalCodigo,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to create a turn from the public kiosk (no auth required)
 */
export function useCrearTurnoPublico(callbacks?: MutationCallbacks<Turno>) {
  return useMutation({
    mutationFn: crearTurnoPublico,
    onSuccess: (data) => callbacks?.onSuccess?.(data),
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// TURNO QUERIES
// =============================================================================

/**
 * Hook to get a single turn by ID
 */
export function useTurno(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.turnos.detail(id),
    queryFn: () => getTurno(id),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to get queue panel (turns waiting ordered by priority)
 */
export function usePanelTurnos(
  sucursalId: number,
  fecha?: string,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery({
    queryKey: queryKeys.turnos.panel(sucursalId, fecha),
    queryFn: () => getPanelTurnos(sucursalId, fecha),
    enabled: (options?.enabled ?? true) && sucursalId > 0,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to get turn statistics
 */
export function useEstadisticasTurnos(
  sucursalId: number,
  fechaInicio?: string,
  fechaFin?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.turnos.estadisticas(sucursalId, fechaInicio, fechaFin),
    queryFn: () => getEstadisticas(sucursalId, fechaInicio, fechaFin),
    enabled: (options?.enabled ?? true) && sucursalId > 0,
  });
}

/**
 * Hook to get estimated wait time
 */
export function useTiempoEspera(
  sucursalId: number,
  prioridad?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.turnos.tiempoEspera(sucursalId, prioridad),
    queryFn: () => getTiempoEspera(sucursalId, prioridad),
    enabled: (options?.enabled ?? true) && sucursalId > 0,
  });
}

// =============================================================================
// PUNTO ATENCION QUERIES
// =============================================================================

/**
 * Hook to get attention points
 */
export function usePuntosAtencion(sucursalId?: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.turnos.puntosAtencion(sucursalId),
    queryFn: () => getPuntosAtencion(sucursalId),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to get active attention points
 */
export function usePuntosAtencionActivos(sucursalId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.turnos.puntosAtencion(sucursalId), "activos"],
    queryFn: () => getPuntosAtencionActivos(sucursalId),
    enabled: (options?.enabled ?? true) && sucursalId > 0,
  });
}

// =============================================================================
// TURNO MUTATIONS
// =============================================================================

/**
 * Hook to create a new turn
 */
export function useCrearTurno(callbacks?: MutationCallbacks<Turno>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: crearTurno,
    onSuccess: (data) => {
      // Invalidate panel queries for the branch
      queryClient.invalidateQueries({
        queryKey: queryKeys.turnos.all,
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to call a turn for attention
 */
export function useLlamarTurno(callbacks?: MutationCallbacks<Turno>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ turnoId, puntoAtencionId }: { turnoId: number; puntoAtencionId: number }) =>
      llamarTurno(turnoId, puntoAtencionId),
    onSuccess: (data) => {
      // Invalidate all panel queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.turnos.all,
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to finish turn attention
 */
export function useFinalizarTurno(callbacks?: MutationCallbacks<Turno>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: finalizarTurno,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.turnos.all,
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to cancel a turn
 */
export function useCancelarTurno(callbacks?: MutationCallbacks<Turno>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ turnoId, motivo }: { turnoId: number; motivo?: string }) =>
      cancelarTurno(turnoId, motivo),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.turnos.all,
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to recall a turn (increment call counter)
 */
export function useRellamarTurno(callbacks?: MutationCallbacks<Turno>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rellamarTurno,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.turnos.all,
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to update a turn's priority
 */
export function useUpdateTurnoPrioridad(callbacks?: MutationCallbacks<Turno>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      turnoId,
      prioridad,
    }: {
      turnoId: number;
      prioridad: "BAJA" | "MEDIA" | "ALTA";
    }) => updateTurnoPrioridad(turnoId, prioridad),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.turnos.all,
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

// =============================================================================
// PUNTO ATENCION CRUD HOOKS
// =============================================================================

/**
 * Hook to get a single attention point by ID
 */
export function usePuntoAtencion(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.turnos.puntosAtencion(), "detail", id],
    queryFn: () => getPuntoAtencion(id),
    enabled: (options?.enabled ?? true) && id > 0,
  });
}

/**
 * Hook to create a new attention point
 */
export function useCreatePuntoAtencion(callbacks?: MutationCallbacks<PuntoAtencion>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPuntoAtencion,
    onSuccess: (data) => {
      // Invalidate all puntos-atencion queries (any sucursalId)
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "turnos" && query.queryKey[1] === "puntos-atencion",
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to update an attention point
 */
export function useUpdatePuntoAtencion(callbacks?: MutationCallbacks<PuntoAtencion>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PuntoAtencionUpdateData }) =>
      updatePuntoAtencion(id, data),
    onSuccess: (data) => {
      // Invalidate all puntos-atencion queries (any sucursalId)
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "turnos" && query.queryKey[1] === "puntos-atencion",
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to delete an attention point (hard delete)
 */
export function useDeletePuntoAtencion(callbacks?: MutationCallbacks<void>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePuntoAtencion,
    onSuccess: () => {
      // Invalidate all puntos-atencion queries (any sucursalId)
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "turnos" && query.queryKey[1] === "puntos-atencion",
      });
      callbacks?.onSuccess?.(undefined);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to activate an attention point
 */
export function useActivatePuntoAtencion(callbacks?: MutationCallbacks<PuntoAtencion>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: activatePuntoAtencion,
    onSuccess: (data) => {
      // Invalidate all puntos-atencion queries (any sucursalId)
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "turnos" && query.queryKey[1] === "puntos-atencion",
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook to deactivate an attention point (soft delete)
 */
export function useDeactivatePuntoAtencion(callbacks?: MutationCallbacks<PuntoAtencion>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deactivatePuntoAtencion,
    onSuccess: (data) => {
      // Invalidate all puntos-atencion queries (any sucursalId)
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "turnos" && query.queryKey[1] === "puntos-atencion",
      });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}
